import { useEffect, useState } from 'react'
import { X, Database, Wrench, SlidersHorizontal } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { getAgentIcon } from '@/components/agents/AgentIcons'
import { useAgentMemory, useAgentSkills } from '@/hooks/useAgentInspector'
import type { AgentSummary } from '@/lib/api'
import { MemoryTab, SkillsTab, ConfigTab } from '@/components/agents/AgentInspectorTabs'

export interface InspectorTarget {
  skillName: string
  displayName: string
  emoji: string
  role: string
  accent: string
}

type InspectorTab = 'memory' | 'skills' | 'config'

const TABS: { key: InspectorTab; label: string; icon: React.ReactNode }[] = [
  { key: 'memory', label: 'Memory', icon: <Database className="h-3.5 w-3.5" aria-hidden="true" /> },
  { key: 'skills', label: 'Skills', icon: <Wrench className="h-3.5 w-3.5" aria-hidden="true" /> },
  { key: 'config', label: 'Config', icon: <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" /> },
]

/**
 * Read-only slide-out Inspector for a single agent. Opens from the Agents page
 * when Mark clicks "Inspect" on a directory row. Three tabs — Memory (stored
 * keys + values, sensitive redacted), Skills (tools + decision authority),
 * Config (schedule, model, last run). Dependency-free overlay matching the
 * RejectDialog pattern: fixed backdrop + Escape-to-close, no focus-trap library.
 */
export function AgentInspectorPanel({
  target,
  live,
  onClose,
}: {
  target: InspectorTarget | null
  live: AgentSummary | undefined
  onClose: () => void
}) {
  const [tab, setTab] = useState<InspectorTab>('memory')
  const skillName = target?.skillName ?? null

  // Reset to the Memory tab each time a different agent opens the panel.
  useEffect(() => {
    if (target) setTab('memory')
  }, [target?.skillName])

  useEffect(() => {
    if (!target) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target, onClose])

  const memory = useAgentMemory(skillName)
  const skills = useAgentSkills(skillName)

  if (!target) return null

  const Icon = getAgentIcon(target.skillName) ?? getAgentIcon(target.displayName)

  return (
    <div
      className="fixed inset-0 z-[80] flex justify-end bg-black/60 animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-label={`${target.displayName} inspector`}
      onClick={onClose}
    >
      <div
        className="relative flex h-full w-full max-w-lg flex-col bg-cmd-container shadow-2xl animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ backgroundColor: target.accent }} />

        {/* Header */}
        <div className="flex items-start gap-3 border-b border-cmd-high p-4 pl-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-cmd-high text-base">
            {Icon ? <Icon size={32} /> : <span>{target.emoji}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-sans text-base font-semibold text-white tracking-tight truncate">
              {target.displayName}
            </h2>
            <p className="font-label text-[0.65rem] text-cmd-outline truncate uppercase tracking-wider">
              {target.role}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close inspector"
            className="shrink-0 p-1 text-cmd-outline transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Tab nav */}
        <div className="flex items-center gap-1 border-b border-cmd-high px-3 py-2" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 font-label text-[0.65rem] tracking-wider uppercase transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
                tab === t.key
                  ? 'bg-cmd-high text-primary'
                  : 'text-cmd-outline hover:text-white'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <ScrollArea className="flex-1">
          <div className="p-4 pl-5">
            {tab === 'memory' && (
              <MemoryTab
                data={memory.data}
                isLoading={memory.isLoading}
                error={memory.error as Error | null}
              />
            )}
            {tab === 'skills' && (
              <SkillsTab
                data={skills.data}
                isLoading={skills.isLoading}
                error={skills.error as Error | null}
              />
            )}
            {tab === 'config' && (
              <ConfigTab
                skills={skills.data}
                live={live}
                isLoading={skills.isLoading}
                error={skills.error as Error | null}
              />
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
